function _toConsumableArray(arr) { return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread(); }

function _nonIterableSpread() { throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _iterableToArray(iter) { if (typeof Symbol !== "undefined" && Symbol.iterator in Object(iter)) return Array.from(iter); }

function _arrayWithoutHoles(arr) { if (Array.isArray(arr)) return _arrayLikeToArray(arr); }

function _slicedToArray(arr, i) { return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest(); }

function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(n); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _iterableToArrayLimit(arr, i) { if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return; var _arr = []; var _n = true; var _d = false; var _e = undefined; try { for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) { _arr.push(_s.value); if (i && _arr.length === i) break; } } catch (err) { _d = true; _e = err; } finally { try { if (!_n && _i["return"] != null) _i["return"](); } finally { if (_d) throw _e; } } return _arr; }

function _arrayWithHoles(arr) { if (Array.isArray(arr)) return arr; }

import React, { forwardRef, useRef, useState, useEffect, createRef, useContext } from 'react';
import cn from 'classnames';
import { arrayOf, shape, number, string, func, oneOfType, array, oneOf, bool } from 'prop-types';
import FormCheckbox from '../../atoms/FormCheckbox';
import FormInput from '../../atoms/FormInput';
import { useBreakpoints, useOnClickOutside, useKeyboardTabbed } from '../../hooks';
import Modal from '../Modal';
import FormContext from '../../context/FormContext';
import FormGroupContext from '../../context/FormGroupContext';
import { filterSupportedEventListenerOptions, throttle } from '../../utils'; // An internal key for the optional "Select All" checkbox. The leading
// underscore is there to help avoid clashes with "real" option values.

var SELECT_ALL_VALUE = '_select-all';

var parseArray = function parseArray(value) {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  return JSON.parse(value);
};
/**
 * FormMultiSelect component.
 */


var _ref2 = /*#__PURE__*/React.createElement("li", {
  className: "form-multi-select__list-item form-multi-select__no-options-item body-copy-small"
}, /*#__PURE__*/React.createElement("span", {
  className: "form-multi-select__list-item-inner"
}, "No results"));

var _ref3 = /*#__PURE__*/React.createElement("span", {
  className: "sr-only"
}, "Toggle options display");

var _ref4 = /*#__PURE__*/React.createElement("span", {
  className: "form-multi-select__caret-icon p-absolute"
});

var FormMultiSelect = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var className = _ref.className,
      name = _ref.name,
      label = _ref.label,
      defaultValue = _ref.defaultValue,
      includeSelectAll = _ref.includeSelectAll,
      initialOptions = _ref.options,
      onChange = _ref.onChange,
      position = _ref.position,
      selectAllLabel = _ref.selectAllLabel,
      value = _ref.value,
      tabIndex = _ref.tabIndex,
      showInlineLabel = _ref.showInlineLabel;
  var enableFocusStyles = useKeyboardTabbed();

  var _useContext = useContext(FormGroupContext),
      inputId = _useContext.inputId,
      feedbackId = _useContext.feedbackId;

  var _useContext2 = useContext(FormContext),
      errors = _useContext2.errors,
      onFormChange = _useContext2.onChange,
      defaultValues = _useContext2.defaultValues;

  var handleChange = function handleChange(evt) {
    if (onChange) {
      onChange(evt);
    }

    if (onFormChange) {
      onFormChange(evt);
    }
  };

  var hiddenInputRef = useRef(ref);
  var searchInputRef = useRef();
  var dropdownRef = useRef();
  var checkboxRefs = useRef([]);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      showModal = _useState2[0],
      setShowModal = _useState2[1];

  var _useState3 = useState(initialOptions),
      _useState4 = _slicedToArray(_useState3, 2),
      options = _useState4[0],
      setOptions = _useState4[1];

  var _useState5 = useState(''),
      _useState6 = _slicedToArray(_useState5, 2),
      inputValue = _useState6[0],
      setInputValue = _useState6[1];

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      showDropdown = _useState8[0],
      setShowDropdown = _useState8[1];

  var _useState9 = useState(null),
      _useState10 = _slicedToArray(_useState9, 2),
      selectedOption = _useState10[0],
      setSelectedOption = _useState10[1]; // Use value for a controlled and defaultValue for an uncontrolled element.
  // https://reactjs.org/docs/forms.html#controlled-components


  var finalValues = parseArray(value || defaultValue || (defaultValues === null || defaultValues === void 0 ? void 0 : defaultValues[name])); // Let the value be passed as a JSON-stringified or regular array.

  var _useState11 = useState(finalValues),
      _useState12 = _slicedToArray(_useState11, 2),
      checkedValues = _useState12[0],
      setCheckedValues = _useState12[1]; // Update checked values if a new value is passed in.


  useEffect(function () {
    setCheckedValues(finalValues);
  }, [JSON.stringify(parseArray(value))]);

  var _useBreakpoints = useBreakpoints(),
      isAboveBreakpoint = _useBreakpoints.isAboveBreakpoint;

  var isModalView = !isAboveBreakpoint('sm', false); // Filter the available options according to what has been typed in the
  // input box.

  var filteredOptions = options.filter(function (option) {
    var isFiltered = !inputValue || option.label.toLowerCase().includes(inputValue.toLowerCase()); // If the selected option has been filtered out deselect it.

    if (!isFiltered && option === selectedOption) {
      setSelectedOption(null);
    }

    return isFiltered;
  });
  var allChecked = checkedValues.length === options.length;
  var noneChecked = !checkedValues.length;

  if (includeSelectAll) {
    filteredOptions.unshift({
      label: selectAllLabel,
      value: SELECT_ALL_VALUE,
      indeterminate: !allChecked && !noneChecked
    });
  } // Update refs if filtered options change.


  useEffect(function () {
    checkboxRefs.current = options.map(function (_, i) {
      var _checkboxRefs$current;

      return (_checkboxRefs$current = checkboxRefs.current[i]) !== null && _checkboxRefs$current !== void 0 ? _checkboxRefs$current : /*#__PURE__*/createRef();
    });
  }, [filteredOptions]); // Update options if changed.

  useEffect(function () {
    setOptions(initialOptions);
  }, [initialOptions]);
  /**
   * Position the dropdown above or below the input, depending on what looks best.
   */

  var positionDropdown = function positionDropdown() {
    var dropdownEl = dropdownRef.current;
    var inputEl = searchInputRef.current;
    var originalDisplay = dropdownEl.style.display; // In case the dropdown is collapsed display it so we can grap the height.

    dropdownEl.style.display = 'flex';

    var _dropdownEl$getBoundi = dropdownEl.getBoundingClientRect(),
        dropdownHeight = _dropdownEl$getBoundi.height;

    var _inputEl$getBoundingC = inputEl.getBoundingClientRect(),
        inputY = _inputEl$getBoundingC.y,
        inputHeight = _inputEl$getBoundingC.height;

    var spaceBelow = window.innerHeight - (inputY + inputHeight);
    var spaceAbove = inputY;
    var fitsBelow = spaceBelow >= dropdownHeight; // Reset the original display value so it doesn't actually render.

    dropdownEl.style.display = originalDisplay;
    var aboveTransform = "translateY(-".concat(dropdownHeight + inputHeight, "px)");
    var belowTransform = 'translateY(0px)';

    if (position === 'above') {
      dropdownEl.style.transform = aboveTransform;
      return;
    }

    if (position === 'below') {
      dropdownEl.style.transform = belowTransform;
      return;
    } // Auto


    dropdownEl.style.transform = !fitsBelow && spaceAbove > spaceBelow ? aboveTransform : belowTransform;
  };
  /**
   * Get a unique ID for the given option.
   */


  var getOptionId = function getOptionId(option) {
    return [inputId || name, option === null || option === void 0 ? void 0 : option.value].filter(function (x) {
      return x;
    }).join('-') || null;
  }; // Scroll to the selected option within the dropdown for the case where the
  // list is long and we're keyboard navigating.


  useEffect(function () {
    if (!selectedOption) {
      return;
    }

    var parentRect = dropdownRef.current.getBoundingClientRect();
    var optionId = getOptionId(selectedOption);
    var listItem = document.getElementById(optionId);
    var childRect = listItem.getBoundingClientRect();
    var isViewable = childRect.top >= parentRect.top && childRect.bottom <= parentRect.top + parentRect.height;

    if (!isViewable) {
      var scrollTop = childRect.top - parentRect.top;
      var scrollBottom = childRect.bottom - parentRect.bottom; // Align the child element to the top or bottom of the container depending
      // on which is closer.

      if (Math.abs(scrollTop) < Math.abs(scrollBottom)) {
        dropdownRef.current.scrollTop += scrollTop;
        return;
      }

      dropdownRef.current.scrollTop += scrollBottom;
    }
  }, [selectedOption, dropdownRef]);
  /**
   * Open the dropdown, positioning it first.
   */

  var toggleDropdown = function toggleDropdown(show) {
    if (show) {
      positionDropdown();
    }

    setShowDropdown(show);
  };
  /**
   * Fire an input event manually.
   */


  var fireInputEvent = function fireInputEvent(newCheckedValues) {
    if (!hiddenInputRef.current) {
      return;
    } // The value is set directly here so that it is correct when the event is
    // picked up.


    hiddenInputRef.current.value = JSON.stringify(newCheckedValues);
    hiddenInputRef.current.dispatchEvent(new Event('input', {
      bubbles: true
    }));
  };
  /**
   * Handle a change of text in the search input.
   */


  var onTextInputChange = function onTextInputChange(evt) {
    evt.preventDefault();
    setInputValue(evt.target.value); // Show the dropdown if something was entered in the search box.

    if (evt.target.value) {
      setShowDropdown(true);
    }
  };
  /**
   * Handle a key down event on the search input.
   *
   * See https://www.w3.org/TR/wai-aria-1.1/#combobox
   */


  var onTextInputKeyDown = function onTextInputKeyDown(evt) {
    var keyCode = evt.keyCode; // Open the dropdown when the down key is pressed, if not already open.

    if (!showDropdown && keyCode === 40) {
      toggleDropdown(true);
      setSelectedOption(filteredOptions[0]);
      return;
    }

    var selectedIndex = filteredOptions.findIndex(function (opt) {
      return opt.value === (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.value);
    });
    var lastIndex = filteredOptions.length - 1; // Move to the next item when the down key is pressed.

    if (keyCode === 40) {
      var nextIndex = selectedIndex === lastIndex ? selectedIndex : selectedIndex + 1;
      setSelectedOption(filteredOptions[nextIndex]);
      return;
    } // Move to the previous item when the up key is pressed.


    if (keyCode === 38) {
      var prevIndex = selectedIndex === 0 ? selectedIndex : selectedIndex - 1;
      setSelectedOption(filteredOptions[prevIndex]);
      return;
    } // Move to the first item when the home key is pressed.


    if (keyCode === 36) {
      setSelectedOption(filteredOptions[0]);
      return;
    } // Move to the last item when the end key is pressed.


    if (keyCode === 35) {
      setSelectedOption(filteredOptions[lastIndex]);
      return;
    } // Select an option when the enter key is pressed.


    if (keyCode === 13) {
      checkboxRefs.current[selectedIndex].current.click();
      setSelectedOption(null);
      return;
    } // Close the dropdown when the esc or tab keys are pressed.


    if (keyCode === 27 || keyCode === 9) {
      toggleDropdown(false);
      setShowModal(false);
      setSelectedOption(null);
    }
  };
  /**
   * Handle a click on the search input.
   */


  var toggleCombobox = function toggleCombobox() {
    if (isModalView) {
      setShowModal(true);
      return;
    }

    toggleDropdown(!showDropdown);
  };
  /**
   * Handle an option being checked or unchecked.
   */


  var onCheckboxChange = function onCheckboxChange(evt) {
    var target = evt.target;
    var allValues = options.map(function (option) {
      return option.value;
    });
    var selectAllValues = allChecked ? [] : allValues;

    if (target.value === SELECT_ALL_VALUE) {
      setCheckedValues(selectAllValues);
      fireInputEvent(selectAllValues);
      return;
    }

    var newCheckedValues = _toConsumableArray(checkedValues).filter(function (val) {
      return String(val) !== target.value;
    });

    if (target.checked) {
      newCheckedValues.push(target.value);
    }

    setCheckedValues(newCheckedValues);
    fireInputEvent(newCheckedValues);
  };
  /**
   * Check if a checkbox is checked.
   */


  var isChecked = function isChecked(option) {
    if (option.value === SELECT_ALL_VALUE) {
      return allChecked;
    }

    return checkedValues.includes(String(option.value));
  }; // Hide the dropdown and modal display if a screen resize crosses the
  // relevant breakpoint.


  useEffect(function () {
    toggleDropdown(false);
    setShowModal(false);
  }, [isModalView]); // Hide on click outside the dropdown.

  useOnClickOutside(dropdownRef, function () {
    if (!showDropdown) {
      return;
    }

    toggleDropdown(false);
  }, [showDropdown]); // When open, reposition the dropdown on scroll or resize.

  useEffect(function () {
    if (!showDropdown) {
      return function () {};
    }

    var handleScrollOrResize = throttle(positionDropdown, 100);
    var evtOpts = filterSupportedEventListenerOptions({
      passive: true
    });
    window.addEventListener('scroll', handleScrollOrResize, evtOpts);
    window.addEventListener('resize', handleScrollOrResize, evtOpts);
    return function () {
      window.removeEventListener('scroll', handleScrollOrResize);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [showDropdown]);
  var listBoxId = "".concat(inputId || name, "-listbox");
  var list = /*#__PURE__*/React.createElement("ul", {
    id: listBoxId,
    className: "ma-reset pa-reset",
    role: "listbox",
    "aria-label": "options"
  }, filteredOptions.map(function (option, i) {
    var isSelected = option.value === (selectedOption === null || selectedOption === void 0 ? void 0 : selectedOption.value);
    return /*#__PURE__*/React.createElement("li", {
      id: getOptionId(option),
      key: option.value,
      className: "form-multi-select__list-item d-flex" + (isSelected ? " form-multi-select__list-item--selected" : ""),
      role: "option",
      "aria-selected": isSelected,
      tabIndex: "-1",
      "data-item-name": option.label
    }, /*#__PURE__*/React.createElement(FormGroupContext.Provider, {
      value: {}
    }, /*#__PURE__*/React.createElement(FormCheckbox, {
      fluid: true,
      rightToLeft: true,
      ref: checkboxRefs.current[i],
      className: "form-multi-select__list-item-inner align-items-center",
      label: option.count ? "".concat(option.label, " (").concat(option.count, ")") : option.label,
      indeterminate: option.indeterminate,
      onChange: onCheckboxChange,
      value: option.value,
      checked: isChecked(option),
      tabIndex: "-1"
    })));
  }), !filteredOptions.length && _ref2);
  return /*#__PURE__*/React.createElement("div", {
    className: cn('form-multi-select', className, showDropdown && 'form-multi-select--open', enableFocusStyles && 'form-multi-select--enable-focus-styles'),
    "aria-expanded": !!(showModal || showDropdown) // Our current version of jsx-a11y claims a combobox must have an
    // aria-controls attribute, which is not the case according to
    // https://www.w3.org/TR/wai-aria-1.1/#combobox
    // (the inner textbox/searchbox has the aria-controls attribute).
    //
    // eslint-disable-next-line jsx-a11y/role-has-required-aria-props
    ,
    role: "combobox"
  }, /*#__PURE__*/React.createElement("input", {
    type: "hidden",
    name: name,
    value: JSON.stringify(checkedValues),
    onChange: function onChange() {} // noop
    ,
    ref: hiddenInputRef,
    onInput: handleChange,
    "aria-describedby": feedbackId
  }), /*#__PURE__*/React.createElement(FormInput, {
    className: "form-multi-select__searchbox",
    role: "searchbox",
    name: "".concat(name, ":searchbox"),
    hasError: !!(errors === null || errors === void 0 ? void 0 : errors[name]),
    "aria-haspopup": "listbox",
    "aria-controls": listBoxId,
    "aria-activedescendant": getOptionId(selectedOption),
    "aria-label": "Filter ".concat(label),
    ref: searchInputRef,
    onChange: onTextInputChange,
    onClick: toggleCombobox,
    onKeyDown: onTextInputKeyDown,
    placeholder: checkedValues.length ? "".concat(checkedValues.length, " selected") : 'Select...',
    autoComplete: "off",
    tabIndex: tabIndex,
    inlineLabel: showInlineLabel ? label : null,
    inlineLabelSelected: !!checkedValues.length
  }), /*#__PURE__*/React.createElement("button", {
    className: "form-multi-select__caret-button p-absolute",
    onClick: toggleCombobox,
    type: "button",
    tabIndex: tabIndex
  }, _ref3, _ref4), /*#__PURE__*/React.createElement("div", {
    ref: dropdownRef,
    className: "form-multi-select__options"
  }, !isModalView && list), /*#__PURE__*/React.createElement(Modal, {
    show: showModal,
    onClose: function onClose() {
      return setShowModal(false);
    }
  }, /*#__PURE__*/React.createElement(Modal.Header, null, label), /*#__PURE__*/React.createElement(Modal.Body, null, isModalView && list)));
});
FormMultiSelect.displayName = 'FormMultiSelect';
FormMultiSelect.defaultProps = {
  className: null,
  options: [],
  onChange: null,
  includeSelectAll: false,
  defaultValue: null,
  position: 'auto',
  selectAllLabel: 'Select All',
  value: null,
  tabIndex: null,
  showInlineLabel: false
};
FormMultiSelect.propTypes = process.env.NODE_ENV !== "production" ? {
  /** Additional class names to add to the component root. */
  className: string,

  /** A name for the input. */
  name: string.isRequired,

  /** Include a "Select All" input as the first option. */
  includeSelectAll: bool,

  /** A label for the input (used in the modal). */
  label: string.isRequired,

  /** The selectable options. */
  options: arrayOf(shape({
    /** The value of the item. */
    value: oneOfType([number, string]),

    /** A label for the item. */
    label: string
  })),

  /** On change event handler. */
  onChange: func,

  /** The default value for the input field, as comma separated filter values. */
  defaultValue: oneOfType([string, array]),

  /** The position of the expanded dropdown. */
  position: oneOf(['above', 'below', 'auto']),

  /** An alternative label for the select all option (if used). */
  selectAllLabel: string,

  /** The value for when using as a controlled component. */
  value: oneOfType([string, array]),

  /** Sets whether the input is accessible via keyboard navigation */
  tabIndex: string,

  /** Sets whether the inline label is used */
  showInlineLabel: bool
} : {};
export default FormMultiSelect;